// Nhận diện thời điểm trong câu tiếng Việt ("mai 9h họp", "thứ sáu 14:30", "20/11 lúc 8 giờ rưỡi", "30 phút nữa") để gợi ý
// tạo nhắc hẹn kiểu Zalo. Module thuần (chỉ cú pháp TS xóa được) để chạy được trong unit test của Node.

export interface DetectedTime {
  /** Thời điểm nhắc (giờ máy). Luôn ở tương lai so với `now`. */
  at: Date;
  /** Đoạn văn bản đã nhận diện (để hiển thị / làm tiêu đề). */
  matched: string;
  /** Chỉ có ngày, không có giờ → đã đặt 09:00. */
  dateOnly: boolean;
}

const L = '(?<![\\p{L}\\p{N}])';
const R = '(?![\\p{L}\\p{N}])';
const re = (src: string) => new RegExp(`${L}(?:${src})${R}`, 'iu');

// Giờ: "9h", "9h30", "9:30", "9 giờ", "9 giờ 30", "9 giờ rưỡi", kèm buổi (sáng / trưa / chiều / tối / đêm) hoặc am / pm.
const PERIOD = '(?:\\s*(sáng|trưa|chiều|tối|đêm|am|pm))?';
const TIME_H = new RegExp(`${L}(\\d{1,2})\\s*(?:h|giờ)\\s*(?:(\\d{2})(?!\\d)|(rưỡi))?${PERIOD}${R}`, 'iu');
const TIME_COLON = new RegExp(`${L}(\\d{1,2}):(\\d{2})${PERIOD}${R}`, 'iu');
const REL = re('(\\d{1,3}|nửa|một|hai|ba)\\s*(phút|p|giờ|tiếng|ngày)\\s*(?:nữa|tới|sau)|sau\\s*(\\d{1,3}|nửa|một|hai|ba)\\s*(phút|giờ|tiếng|ngày)');
const D_TODAY = re('hôm nay|bữa nay');
const D_TOMORROW = re('ngày mai|mai');
const D_AFTER = re('ngày kia|ngày mốt|mốt');
const D_WEEKDAY = re('(?:thứ|thu)\\s*(hai|ba|tư|bốn|năm|sáu|bảy|[2-7])|chủ\\s*nhật|cn');
const NEXT_WEEK = re('tuần\\s*(?:sau|tới|tiếp)');
const D_SLASH = re('(\\d{1,2})\\s*/\\s*(\\d{1,2})(?:\\s*/\\s*(\\d{4}|\\d{2}))?');
const D_WORDS = re('ngày\\s*(\\d{1,2})(?:\\s*(?:tháng|th|/)\\s*(\\d{1,2}))?(?:\\s*(?:năm)\\s*(\\d{4}))?');

const WORD_NUM: Record<string, number> = { nửa: 0.5, một: 1, hai: 2, ba: 3 };
const WEEKDAY: Record<string, number> = { hai: 1, ba: 2, tư: 3, bốn: 3, năm: 4, sáu: 5, bảy: 6, '2': 1, '3': 2, '4': 3, '5': 4, '6': 5, '7': 6 };

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
}
function validDate(y: number, m: number, d: number): boolean {
  const t = new Date(y, m - 1, d);
  return t.getFullYear() === y && t.getMonth() === m - 1 && t.getDate() === d;
}

function applyPeriod(h: number, period: string | undefined): number {
  const p = period?.toLowerCase();
  if (!p) return h;
  if (p === 'pm' || p === 'chiều' || p === 'tối') return h < 12 ? h + 12 : h;
  if (p === 'đêm') return h < 12 ? (h >= 6 ? h + 12 : h) : h; // "11 giờ đêm" = 23h, "2 giờ đêm" = 2h
  if (p === 'trưa') return h < 11 ? h + 12 : h; // "1 giờ trưa" = 13h, "12 giờ trưa" = 12h
  return h === 12 && (p === 'sáng' || p === 'am') ? 0 : h;
}

function findTime(text: string): { h: number; min: number; matched: string; explicitPeriod: boolean } | undefined {
  const a = TIME_H.exec(text);
  if (a) {
    const h0 = Number(a[1]);
    const min = a[3] ? 30 : a[2] ? Number(a[2]) : 0;
    const h = applyPeriod(h0, a[4]);
    if (h0 <= 24 && min < 60) return { h: h % 24, min, matched: a[0], explicitPeriod: !!a[4] };
  }
  const b = TIME_COLON.exec(text);
  if (b) {
    const h0 = Number(b[1]);
    const min = Number(b[2]);
    if (h0 <= 24 && min < 60) return { h: applyPeriod(h0, b[3]) % 24, min, matched: b[0], explicitPeriod: !!b[3] };
  }
  return undefined;
}

function findDate(text: string, now: Date): { date: Date; matched: string } | undefined {
  const today = startOfDay(now);
  let m = D_SLASH.exec(text);
  if (m) {
    const d = Number(m[1]);
    const mo = Number(m[2]);
    let y = m[3] ? Number(m[3]) : today.getFullYear();
    if (m[3] && m[3].length === 2) y += 2000;
    if (mo >= 1 && mo <= 12 && d >= 1 && validDate(y, mo, d)) {
      let date = new Date(y, mo - 1, d);
      // Không ghi năm mà ngày đã qua → năm sau.
      if (!m[3] && date < today) date = new Date(y + 1, mo - 1, d);
      return { date, matched: m[0] };
    }
  }
  m = D_WORDS.exec(text);
  if (m) {
    const d = Number(m[1]);
    const hasMonth = !!m[2];
    const mo = hasMonth ? Number(m[2]) : today.getMonth() + 1;
    let y = m[3] ? Number(m[3]) : today.getFullYear();
    if (mo >= 1 && mo <= 12 && d >= 1 && validDate(y, mo, d)) {
      let date = new Date(y, mo - 1, d);
      if (!m[3] && date < today) date = hasMonth ? new Date(y + 1, mo - 1, d) : validDate(y, mo + 1, d) ? new Date(y, mo, d) : date;
      return { date, matched: m[0] };
    }
  }
  m = D_AFTER.exec(text);
  if (m) return { date: addDays(today, 2), matched: m[0] };
  m = D_TOMORROW.exec(text);
  if (m) return { date: addDays(today, 1), matched: m[0] };
  m = D_WEEKDAY.exec(text);
  if (m) {
    const key = (m[1] ?? '').toLowerCase();
    const target = m[1] ? WEEKDAY[key] : 0; // 0 = Chủ nhật
    if (target === undefined) return undefined;
    const nextWeek = NEXT_WEEK.exec(text);
    const diff = (target - today.getDay() + 7) % 7;
    if (nextWeek) {
      // "thứ sáu tuần sau": thứ đó của tuần kế tiếp (tuần bắt đầu từ thứ Hai).
      const mondayThisWeek = addDays(today, -((today.getDay() + 6) % 7));
      const offset = (target + 6) % 7;
      return { date: addDays(mondayThisWeek, 7 + offset), matched: `${m[0]} ${nextWeek[0]}` };
    }
    return { date: addDays(today, diff), matched: m[0] };
  }
  m = D_TODAY.exec(text);
  if (m) return { date: today, matched: m[0] };
  return undefined;
}

/**
 * Tìm thời điểm nhắc trong `text`; trả `undefined` nếu không có hoặc đã qua.
 * `now`: mốc để hiểu "mai", "thứ sáu"… (với tin cũ là lúc gửi tin); `after`: thời điểm tìm được phải sau mốc này (mặc định = `now`).
 */
export function detectTime(text: string, now: Date = new Date(), after: Date = now): DetectedTime | undefined {
  if (!text || text.length > 600) return undefined;
  // Nói về quá khứ ("hôm qua họp lúc 9h") → không gợi ý.
  if (re('hôm qua|hôm kia|bữa qua|tuần trước|tháng trước|hồi (?:sáng|trưa|chiều|tối)|lúc nãy|vừa (?:rồi|mới)|đã (?:họp|gặp|xong)').test(text)) return undefined;

  // "30 phút nữa", "sau 2 giờ", "nửa tiếng nữa".
  const rel = REL.exec(text);
  if (rel) {
    const raw = rel[1] ?? rel[3] ?? '';
    const unit = (rel[2] ?? rel[4] ?? '').toLowerCase();
    const n = WORD_NUM[raw.toLowerCase()] ?? Number(raw);
    const minutes = unit === 'phút' || unit === 'p' ? n : unit === 'ngày' ? n * 1440 : n * 60;
    if (n > 0 && minutes > 0 && minutes <= 60 * 24 * 60) {
      const at = new Date(now.getTime() + Math.round(minutes) * 60_000);
      at.setSeconds(0, 0);
      return { at, matched: rel[0], dateOnly: false };
    }
  }

  const time = findTime(text);
  const date = findDate(text, now);
  if (!time && !date) return undefined;
  // Con số trần như "9h" cần có động từ / ngữ cảnh? Chấp nhận: giờ có "h/giờ/:" là đủ rõ.

  let at: Date;
  if (date && time) {
    at = new Date(date.date.getFullYear(), date.date.getMonth(), date.date.getDate(), time.h, time.min);
    // Giờ 1–11 không ghi buổi, trong ngày hôm nay đã qua → hiểu là buổi chiều/tối nếu còn ở tương lai.
    if (at <= now && !time.explicitPeriod && time.h >= 1 && time.h <= 11 && date.date.getTime() === startOfDay(now).getTime()) {
      const pm = new Date(at.getTime() + 12 * 3600_000);
      if (pm > now) at = pm;
    }
  } else if (time) {
    at = new Date(now.getFullYear(), now.getMonth(), now.getDate(), time.h, time.min);
    if (at <= now && !time.explicitPeriod && time.h >= 1 && time.h <= 11) {
      const pm = new Date(at.getTime() + 12 * 3600_000);
      if (pm > now) at = pm;
    }
    if (at <= now) at = addDays(at, 1); // chỉ có giờ và đã qua hôm nay → ngày mai
  } else {
    at = new Date(date!.date.getFullYear(), date!.date.getMonth(), date!.date.getDate(), 9, 0);
  }
  if (at <= after) return undefined;

  const matched = [date?.matched, time?.matched].filter(Boolean).join(' ');
  return { at, matched, dateOnly: !time };
}

/** Nhãn ngắn cho gợi ý: "14:00 · Thứ Sáu 06/11" / "Hôm nay 14:00". */
export function suggestionLabel(d: DetectedTime, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const days = ['Chủ nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
  const diff = Math.round((startOfDay(d.at).getTime() - startOfDay(now).getTime()) / 86_400_000);
  const day = diff === 0 ? 'Hôm nay' : diff === 1 ? 'Ngày mai' : `${days[d.at.getDay()]} ${pad(d.at.getDate())}/${pad(d.at.getMonth() + 1)}`;
  return `${pad(d.at.getHours())}:${pad(d.at.getMinutes())} · ${day}`;
}
